import React from 'react';
import { loadStripe, type Stripe, type StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import Banner from './shared/Banner';
import * as analytics from '../../lib/analytics';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

// CTV-518: US-only card policy. Our Stripe-fee math (CTV-635: estimated per
// charge and deducted from the publisher's payout) only covers domestic-card
// Stripe fees, so we decline non-US cards. We read the issuing
// country from a confirmation-token preview BEFORE confirming — that tokenizes
// the entered card without authenticating (no 3-D Secure redirect) or placing a
// hold, so a non-US card is rejected instantly with a clear message. The API
// (finalize + webhook) enforces the same rule as a backstop.
export const US_ONLY_CARD_MESSAGE =
  'We currently accept U.S.-issued payment cards only. Please use a card issued in the United States.';

// CTV-518: CTVBuyer is the merchant of record — every PaymentIntent is a
// plain platform charge on our Stripe account (publisher payouts happen
// later via separate transfers). Stripe.js must NOT be scoped with
// `stripeAccount`; that's only for direct charges on a connected account.
let stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise() {
  if (!stripePromise && publishableKey) stripePromise = loadStripe(publishableKey);
  return stripePromise;
}

interface CommonSectionProps {
  accentColor: string;
  amountCents: number;
  disabled?: boolean;
  disabledReason?: string;
  submitLabel?: string;
  beforeButton?: React.ReactNode;
  onSuccess: () => void;
  onError: (msg: string) => void;
  // CTV-637: extra dimensions merged into the PAYMENT_CONFIRM_FAILED event so
  // a confirm failure can be attributed to a station / plan / surface. Codes
  // and categories only — never raw Stripe messages or buyer payloads.
  analyticsContext?: Record<string, unknown>;
}

// Immediate mode: the caller already created the intent and hands us its
// client secret. The Payment Element mounts from that secret and confirms
// against it. Used by the extend / update-payment / payment-recovery surfaces,
// where exactly one intent exists and there's no plan toggle to churn it.
interface ImmediateSectionProps extends CommonSectionProps {
  clientSecret: string;
  intentType?: 'payment' | 'setup';
  mode?: never;
  createIntent?: never;
}

// CTV-587/588 — Deferred mode: the Payment Element mounts from {mode, amount,
// currency} with NO client secret, so toggling the payment plan (upfront ↔
// monthly) updates it in place via elements.update() instead of remounting.
// That keeps the buyer's entered card across a toggle (587), and no intent
// exists until Pay, so switching plans no longer orphans a canceled one (588).
// `createIntent` is called once, on Pay, after the US-card pre-check passes;
// it resolves null when the caller surfaced its own blocker (e.g. an inventory
// regression) and payment should quietly abort.
interface DeferredSectionProps extends CommonSectionProps {
  mode: 'payment' | 'setup';
  createIntent: () => Promise<{ clientSecret: string; intentType: 'payment' | 'setup' } | null>;
  // CTV-587/588: a Stripe CustomerSession secret enables the Payment Element's
  // "save this card" checkbox + redisplay of the buyer's saved cards. Optional
  // — anonymous / not-yet-resolved buyers just get plain card entry.
  customerSessionClientSecret?: string;
  clientSecret?: never;
  intentType?: never;
}

type StripePaymentSectionProps = ImmediateSectionProps | DeferredSectionProps;

const StripePaymentSection: React.FC<StripePaymentSectionProps> = (props) => {
  const { accentColor, amountCents, disabled, disabledReason, submitLabel, beforeButton, onSuccess, onError, analyticsContext } = props;
  const deferred = props.createIntent != null;
  // The confirm-method selector + button/messaging copy. Immediate mode reads
  // it from the passed intent; deferred mode from the plan-derived mode.
  const mode: 'payment' | 'setup' = deferred ? props.mode : (props.intentType ?? 'payment');
  const customerSessionClientSecret = deferred ? props.customerSessionClientSecret : undefined;

  // Elements options. Immediate mode mounts from the client secret. Deferred
  // mode mounts from {mode, amount, currency}; memoized so identity only
  // changes when mode/amount change, so react-stripe-js applies the delta via
  // elements.update() (every option is mutable except clientSecret/fonts)
  // rather than remounting.
  //
  // `setupFutureUsage` MUST be set explicitly in BOTH modes, because
  // react-stripe-js's update path only sends keys present in the new options —
  // it can't unset a key you omit. `payment` must therefore pass
  // `setupFutureUsage: null` (matching the upfront PaymentIntent, which has no
  // setup_future_usage) to CLEAR the `off_session` left behind after a
  // monthly→upfront toggle; without it, confirming the hold fails with
  // "setup_future_usage (null) does not match the expected (off_session)".
  // `setup` carries `off_session` to match the monthly SetupIntent's usage.
  // The stale `amount`/`captureMethod` left on the group after an upfront→
  // monthly toggle are inert for a SetupIntent, so they need no clearing.
  //
  // `paymentMethodTypes: ['card']` is REQUIRED in both modes: both server
  // intents pin `payment_method_types: ['card']`, and a deferred Element that
  // omits paymentMethodTypes collects via automatic/dynamic methods — which
  // can't be confirmed against an intent that specifies explicit types
  // ("Payment details were collected … using automatic payment methods and
  // cannot be confirmed …"). The flow is US-card-only anyway, so card is the
  // only method.
  // CTV-587/588: make Stripe's built-in "Save payment details" checkbox stand
  // out — it renders inside the Element iframe, so it can only be styled via
  // the Appearance API. Bolder, larger, accent-colored label + accent checkbox.
  // (A tinted container box isn't possible — Stripe's `.Checkbox` selector
  // doesn't accept a background/border.)
  const appearance = React.useMemo(
    () => ({
      rules: {
        '.CheckboxLabel': { fontWeight: '600', fontSize: '15px', color: '#0f172a' },
        '.CheckboxInput': { borderColor: accentColor },
        '.CheckboxInput--checked': { backgroundColor: accentColor, borderColor: accentColor },
      },
    }),
    [accentColor],
  );

  const options = React.useMemo<StripeElementsOptions>(
    () =>
      deferred
        ? mode === 'setup'
          ? { mode: 'setup', currency: 'usd', setupFutureUsage: 'off_session', paymentMethodTypes: ['card'], appearance, ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}) }
          : { mode: 'payment', amount: amountCents, currency: 'usd', captureMethod: 'manual', setupFutureUsage: null, paymentMethodTypes: ['card'], appearance, ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}) }
        : { clientSecret: props.clientSecret },
    [deferred, mode, amountCents, props.clientSecret, customerSessionClientSecret, appearance],
  );

  if (!publishableKey) {
    return (
      <Banner tone="caution" label="Stripe not configured">
        Set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in <code>apps/selfservice/.env.local</code> and restart the dev server.
      </Banner>
    );
  }

  return (
    <Elements stripe={getStripePromise()} options={options}>
      <Inner mode={mode} accentColor={accentColor} amountCents={amountCents} disabled={disabled} disabledReason={disabledReason} submitLabel={submitLabel} beforeButton={beforeButton} createIntent={props.createIntent} onSuccess={onSuccess} onError={onError} analyticsContext={analyticsContext} />
    </Elements>
  );
};

const Inner: React.FC<{ mode: 'payment' | 'setup'; accentColor: string; amountCents: number; disabled?: boolean; disabledReason?: string; submitLabel?: string; beforeButton?: React.ReactNode; createIntent?: () => Promise<{ clientSecret: string; intentType: 'payment' | 'setup' } | null>; onSuccess: () => void; onError: (msg: string) => void; analyticsContext?: Record<string, unknown> }> = ({ mode, accentColor, amountCents, disabled, disabledReason, submitLabel, beforeButton, createIntent, onSuccess, onError, analyticsContext }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = React.useState(false);
  const [cardComplete, setCardComplete] = React.useState(false);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const submitResult = await elements.submit();
    if (submitResult.error) {
      setBusy(false);
      onError(submitResult.error.message ?? 'Check your payment details and try again');
      return;
    }
    // CTV-518: US-only card pre-check. Tokenize the entered card (no auth, no
    // hold, no 3-D Secure redirect) just to read the issuing country, and reject
    // a non-US card here with a clear message before we ever confirm.
    const tokenResult = await stripe.createConfirmationToken({ elements });
    if (tokenResult.error) {
      setBusy(false);
      onError(tokenResult.error.message ?? 'Check your payment details and try again');
      return;
    }
    const cardCountry = tokenResult.confirmationToken?.payment_method_preview?.card?.country;
    if (cardCountry && cardCountry.toUpperCase() !== 'US') {
      setBusy(false);
      onError(US_ONLY_CARD_MESSAGE);
      return;
    }
    // Deferred mode: only now (US card verified) do we create the submission
    // row + Stripe intent, and confirm against the returned client secret.
    // Immediate mode: the intent already exists; confirm against the client
    // secret the Elements provider was mounted with (passed implicitly).
    let confirmSecret: string | undefined;
    let confirmType: 'payment' | 'setup' = mode;
    if (createIntent) {
      let created: { clientSecret: string; intentType: 'payment' | 'setup' } | null;
      try {
        created = await createIntent();
      } catch (err) {
        setBusy(false);
        onError(err instanceof Error ? err.message : 'Failed to start payment');
        return;
      }
      if (!created) {
        // The caller surfaced its own blocker (e.g. inventory regression modal);
        // stop quietly rather than double-reporting an error.
        setBusy(false);
        return;
      }
      confirmSecret = created.clientSecret;
      confirmType = created.intentType;
    }
    // In immediate mode confirmSecret stays undefined — Stripe.js reads the
    // ambient client secret the Elements group was created with.
    const confirmArgs = {
      elements,
      ...(confirmSecret ? { clientSecret: confirmSecret } : {}),
      redirect: 'if_required' as const,
      confirmParams: { return_url: window.location.href },
    };
    const result = confirmType === 'setup'
      ? await stripe.confirmSetup(confirmArgs)
      : await stripe.confirmPayment(confirmArgs);
    setBusy(false);

    // CTV-637: every path below this line is a failure that happens AFTER the
    // submission + creative rows and the Stripe intent already exist (in the
    // deferred flow `createIntent` ran a few lines up). Each one therefore
    // strands a `pending_payment` row. None of them were tracked before, which
    // is why the two known orphans in prod went unnoticed for six days. Codes
    // and categories only — `result.error.message` is buyer-facing prose and
    // must not be sent.
    const trackConfirmFailure = (reason: string, extra?: Record<string, unknown>) =>
      analytics.track(analytics.EVENTS.PAYMENT_CONFIRM_FAILED, {
        reason,
        confirm_type: confirmType,
        // Only the deferred flow creates the submission row inside handlePay,
        // so only it can orphan one. Immediate-mode surfaces (extend, payment
        // recovery) confirm against a pre-existing intent.
        orphans_submission: createIntent != null,
        ...analyticsContext,
        ...extra,
      });

    if (result.error) {
      trackConfirmFailure('stripe_error', {
        error_code: result.error.code,
        error_type: result.error.type,
        decline_code: result.error.decline_code,
      });
      onError(result.error.message ?? (confirmType === 'setup' ? 'Unable to save payment method' : 'Payment failed'));
      return;
    }
    if ('setupIntent' in result) {
      if (result.setupIntent?.status === 'succeeded') {
        onSuccess();
      } else {
        trackConfirmFailure('unexpected_setup_status', {
          intent_status: result.setupIntent?.status ?? 'unknown',
        });
        onError(`Unexpected setup status: ${result.setupIntent?.status ?? 'unknown'}`);
      }
      return;
    }
    if (result.paymentIntent && (result.paymentIntent.status === 'requires_capture' || result.paymentIntent.status === 'succeeded')) {
      onSuccess();
    } else {
      trackConfirmFailure('unexpected_payment_status', {
        intent_status: result.paymentIntent?.status ?? 'unknown',
      });
      onError(`Unexpected payment status: ${result.paymentIntent?.status ?? 'unknown'}`);
    }
  };

  // External (terms/refund) reasons take precedence over the card-incomplete
  // hint so the buyer sees the most relevant blocker.
  const buttonDisabled = !stripe || busy || disabled;
  const hint = disabled && disabledReason
    ? disabledReason
    : !cardComplete
      ? mode === 'setup' ? 'Enter your card details before saving your payment method' : 'Enter your card details before authorizing payment'
      : null;

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <PaymentElement
        options={{ layout: 'tabs' }}
        onChange={(e) => setCardComplete(e.complete)}
      />
      {beforeButton}
      <button
        type="submit"
        disabled={buttonDisabled}
        className="w-full px-6 py-3 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: accentColor }}
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitLabel ?? (mode === 'setup'
          ? 'Save card and submit campaign'
          : `Authorize ${(amountCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
      </button>
      {/* Always in the layout (visibility, not conditional render) so the
          hint appearing/clearing never shifts the page — the line stays
          reserved and only the text toggles. */}
      <p
        className="text-xs text-slate-500 text-center"
        aria-hidden={!hint}
        style={hint ? undefined : { visibility: 'hidden' }}
      >
        {hint ?? ' '}
      </p>
    </form>
  );
};

export const StripePaymentActionSection: React.FC<{
  clientSecret: string;
  accentColor: string;
  buttonLabel?: string;
  onSuccess: (paymentIntentId: string | null) => void;
  onError: (msg: string) => void;
}> = ({ clientSecret, accentColor, buttonLabel, onSuccess, onError }) => {
  if (!publishableKey) {
    return (
      <Banner tone="caution" label="Stripe not configured">
        Set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in <code>apps/selfservice/.env.local</code> and restart the dev server.
      </Banner>
    );
  }
  return (
    <Elements stripe={getStripePromise()} options={{ clientSecret }}>
      <PaymentActionInner
        clientSecret={clientSecret}
        accentColor={accentColor}
        buttonLabel={buttonLabel}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

const PaymentActionInner: React.FC<{
  clientSecret: string;
  accentColor: string;
  buttonLabel?: string;
  onSuccess: (paymentIntentId: string | null) => void;
  onError: (msg: string) => void;
}> = ({ clientSecret, accentColor, buttonLabel, onSuccess, onError }) => {
  const stripe = useStripe();
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    if (!stripe) return;
    setBusy(true);
    const result = await stripe.confirmPayment({
      clientSecret,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    setBusy(false);
    if (result.error) {
      onError(result.error.message ?? 'Unable to complete bank verification');
      return;
    }
    if (result.paymentIntent && ['requires_capture', 'succeeded', 'processing'].includes(result.paymentIntent.status)) {
      onSuccess(result.paymentIntent.id ?? null);
    } else {
      onError(`Unexpected payment status: ${result.paymentIntent?.status ?? 'unknown'}`);
    }
  };

  return (
    <button
      type="button"
      disabled={!stripe || busy}
      onClick={handleConfirm}
      className="w-full px-6 py-3 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      style={{ backgroundColor: accentColor }}
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {buttonLabel ?? 'Complete bank verification'}
    </button>
  );
};

export default StripePaymentSection;
